import React, { useState, useEffect } from "react";
import axios from "axios";
import { Upload, X as XIcon, ShieldCheck } from "lucide-react";
import { IdentityVerifiedBadge } from "../../components/badges/IdentityVerifiedBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const IdentityVerification = ({ user, showToast, onBack }) => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    full_name: user?.name || "",
    birth_date: "",
    passport_series_number: "",
    passport_issued_by: "",
    passport_issue_date: "",
    registration_address: "",
    consent_passport_data: false,
  });

  const [passportUpload, setPassportUpload] = useState(null); // {upload_id, filename}
  const [selfieUpload, setSelfieUpload] = useState(null);
  const [passportUploading, setPassportUploading] = useState(false);
  const [selfieUploading, setSelfieUploading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API}/identity-verification/my-status`);
      setStatus(res.data);
    } catch (error) {
      console.error("Failed to load identity verification status:", error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const uploadDocument = async (file, docType) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      showToast("Разрешены только JPG или PNG файлы", "error");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      showToast("Размер файла не должен превышать 15МБ", "error");
      return;
    }
    const setUploading = docType === "passport" ? setPassportUploading : setSelfieUploading;
    const setUpload = docType === "passport" ? setPassportUpload : setSelfieUpload;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/identity-verification/upload-document?doc_type=${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUpload({ upload_id: res.data.upload_id, filename: file.name, metadata: res.data.metadata_analysis });
      showToast(docType === "passport" ? "Фото паспорта загружено" : "Селфи с паспортом загружено", "success");
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка загрузки файла", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.full_name.trim() || !formData.birth_date.trim() || !formData.passport_series_number.trim()) {
      showToast("Заполните ФИО, дату рождения и серию/номер паспорта", "error");
      return;
    }
    if (!passportUpload || !selfieUpload) {
      showToast("Загрузите фото паспорта и селфи с паспортом", "error");
      return;
    }
    if (!formData.consent_passport_data) {
      showToast("Необходимо согласие на обработку паспортных данных", "error");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/identity-verification/submit`, {
        full_name: formData.full_name.trim(),
        birth_date: formData.birth_date.trim(),
        passport_series_number: formData.passport_series_number.trim(),
        passport_issued_by: formData.passport_issued_by.trim() || null,
        passport_issue_date: formData.passport_issue_date.trim() || null,
        registration_address: formData.registration_address.trim() || null,
        passport_upload_id: passportUpload.upload_id,
        selfie_upload_id: selfieUpload.upload_id,
        consent_passport_data: formData.consent_passport_data,
      });
      showToast(res.data.message, "success");
      setPassportUpload(null);
      setSelfieUpload(null);
      loadStatus();
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при отправке заявки", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (s) => {
    switch (s) {
      case "approved": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "rejected": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (s) => {
    switch (s) {
      case "approved": return "Личность подтверждена";
      case "pending": return "На рассмотрении (обычно 1-2 дня)";
      case "rejected": return "Заявка отклонена";
      case "not_requested": return "Заявка не подавалась";
      default: return s;
    }
  };

  const canShowForm = !status || status.status === "not_requested" || status.status === "rejected";
  const isBlockedByCooldown = status?.status === "rejected" && status?.can_resubmit_at && new Date(status.can_resubmit_at) > new Date();

  return (
    <div className="space-y-6" data-testid="identity-verification-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Верификация личности</h2>
        <button onClick={onBack} className="text-blue-600 hover:text-blue-800" data-testid="identity-verification-back-btn">
          ← Назад
        </button>
      </div>

      {/* Текущий статус */}
      {!loadingStatus && status && (
        <div className="bg-white p-6 rounded-lg shadow-md" data-testid="identity-verification-status-card">
          <h3 className="text-lg font-semibold mb-4">Статус заявки</h3>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(status.status)}`} data-testid="identity-verification-status-label">
              {getStatusText(status.status)}
            </span>
            {status.verified && <IdentityVerifiedBadge size="md" />}
          </div>
          {status.status === "rejected" && status.rejection_reason && (
            <div className="mt-4 p-3 bg-red-50 rounded-md" data-testid="identity-verification-rejection-reason">
              <p className="text-sm text-red-700">
                <strong>Причина отклонения:</strong> {status.rejection_reason}
              </p>
              {status.can_resubmit_at && (
                <p className="text-xs text-red-600 mt-1">
                  Повторную заявку можно подать после {new Date(status.can_resubmit_at).toLocaleDateString("ru-RU")}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Форма подачи заявки */}
      {canShowForm && !isBlockedByCooldown && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Подать заявку на верификацию личности</h3>
          <p className="text-sm text-gray-600 mb-6">
            Подтверждение личности повышает доверие заказчиков и открывает доступ к заданиям с доступом в дом,
            на участок или к личным вещам (например, ремонт и сборка, уборка, переезд, курьерская доставка,
            присмотр за питомцем, мемориальный сервис). Заявку проверяет администратор вручную, обычно в течение 1-2 дней.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ФИО (полностью) <span className="text-red-500">*</span></label>
              <input
                type="text"
                required
                data-testid="identity-full-name-input"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Иванов Иван Иванович"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Дата рождения <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  required
                  data-testid="identity-birth-date-input"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Серия и номер паспорта <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  data-testid="identity-passport-number-input"
                  value={formData.passport_series_number}
                  onChange={(e) => setFormData({ ...formData, passport_series_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="4500 123456"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Кем выдан <span className="text-gray-400 text-xs">(опционально)</span></label>
                <input
                  type="text"
                  data-testid="identity-issued-by-input"
                  value={formData.passport_issued_by}
                  onChange={(e) => setFormData({ ...formData, passport_issued_by: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Дата выдачи <span className="text-gray-400 text-xs">(опционально)</span></label>
                <input
                  type="date"
                  data-testid="identity-issue-date-input"
                  value={formData.passport_issue_date}
                  onChange={(e) => setFormData({ ...formData, passport_issue_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Адрес регистрации <span className="text-gray-400 text-xs">(опционально)</span></label>
              <input
                type="text"
                data-testid="identity-address-input"
                value={formData.registration_address}
                onChange={(e) => setFormData({ ...formData, registration_address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Фото паспорта */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Фото первой страницы паспорта <span className="text-red-500">*</span></label>
              {passportUpload ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                  <span className="text-sm text-green-800 truncate">{passportUpload.filename}</span>
                  <button type="button" data-testid="identity-passport-remove-btn" onClick={() => setPassportUpload(null)} className="ml-3 text-red-500 hover:text-red-700">
                    <XIcon size={16} />
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    id="passport-upload"
                    data-testid="identity-passport-file-input"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && uploadDocument(e.target.files[0], "passport")}
                  />
                  <label
                    htmlFor="passport-upload"
                    className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-md cursor-pointer transition-colors ${passportUploading ? "border-blue-300 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}
                  >
                    <Upload size={16} className="text-gray-500" />
                    <span className="text-sm text-gray-600">{passportUploading ? "Загрузка..." : "Выбрать файл"}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Селфи с паспортом */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Селфи с паспортом в руках <span className="text-red-500">*</span></label>
              {selfieUpload ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                  <span className="text-sm text-green-800 truncate">{selfieUpload.filename}</span>
                  <button type="button" data-testid="identity-selfie-remove-btn" onClick={() => setSelfieUpload(null)} className="ml-3 text-red-500 hover:text-red-700">
                    <XIcon size={16} />
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    id="selfie-upload"
                    data-testid="identity-selfie-file-input"
                    className="hidden"
                    onChange={(e) => e.target.files[0] && uploadDocument(e.target.files[0], "selfie")}
                  />
                  <label
                    htmlFor="selfie-upload"
                    className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-md cursor-pointer transition-colors ${selfieUploading ? "border-blue-300 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}
                  >
                    <Upload size={16} className="text-gray-500" />
                    <span className="text-sm text-gray-600">{selfieUploading ? "Загрузка..." : "Выбрать файл"}</span>
                  </label>
                </div>
              )}
            </div>

            {/* Согласие на обработку паспортных данных */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-md">
              <input
                type="checkbox"
                id="consent_passport_data"
                data-testid="identity-consent-checkbox"
                checked={formData.consent_passport_data}
                onChange={(e) => setFormData({ ...formData, consent_passport_data: e.target.checked })}
                className="mt-0.5 rounded border-gray-300 focus:ring-2"
              />
              <label htmlFor="consent_passport_data" className="text-sm text-gray-700">
                Согласен на обработку паспортных данных в целях верификации личности. Фото документов хранятся в
                зашифрованном виде и автоматически удаляются через 30 дней после решения администратора.
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="identity-submit-btn"
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? "Отправка заявки..." : "Отправить на проверку"}
            </button>
          </form>
        </div>
      )}

      {/* Информация */}
      <div className="bg-blue-50 p-6 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
          <ShieldCheck size={18} /> Зачем это нужно
        </h4>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>• Бейдж «Личность подтверждена» повышает доверие заказчиков</li>
          <li>• Открывает доступ к заданиям с меткой «Только для проверенных исполнителей»: ремонт и сборка, уборка, курьер и доставка, грузчик и переезд, присмотр за питомцем, личное поручение, мемориальный сервис — и к любым другим заданиям, где заказчик поставил такую отметку</li>
          <li>• Без верификации отклик и начало обсуждения по таким заданиям недоступны — система вернёт ошибку при попытке принять задание</li>
          <li>• Заявку проверяет администратор вручную, решение приходит в уведомлениях</li>
          <li>• Повторная заявка после отклонения — не раньше чем через 7 дней</li>
        </ul>
      </div>
    </div>
  );
};

export default IdentityVerification;
